import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  MessageSquare,
  Calendar,
  Users,
  FileText,
  Clock,
  ChevronRight,
  Zap,
  ArrowUpRight,
} from "lucide-react";

const RecentActivity = ({ activities }) => {
  const navigate = useNavigate();

  const safeActivities = Array.isArray(activities) ? activities : [];

  const getStatusConfig = (status) => {
    switch (status) {
      case "success":
        return {
          color: "text-green-600",
          bg: "bg-green-50",
          label: "Success",
        };
      case "error":
        return {
          color: "text-red-600",
          bg: "bg-red-50",
          label: "Failed",
        };
      case "pending":
        return {
          color: "text-yellow-600",
          bg: "bg-yellow-50",
          label: "Pending",
        };
      default:
        return {
          color: "text-blue-600",
          bg: "bg-blue-50",
          label: "Info",
        };
    }
  };

  const getActivityIcon = (type) => {
    const icons = {
      message: MessageSquare,
      recurring: Calendar,
      template: FileText,
      group: Users,
    };
    return icons[type] || MessageSquare;
  };

  const getActivityRoute = (activity = {}) => {
    if (activity.route) return activity.route;
    if (activity.path) return activity.path;
    if (activity.url) return activity.url;
    if (activity.link) return activity.link;

    const type = String(activity.type || "").toLowerCase();
    if (type === "template") return "/template";
    if (type === "group") return "/whatsapp-groups-details";
    if (type === "recurring") return "/schedule";
    if (type === "message") return "/whatsapp-message";

    return "/whatsapp-message";
  };

  const handleActivityClick = (activity) => {
    navigate(getActivityRoute(activity));
  };

  const handleViewAll = () => {
    navigate("/whatsapp-message");
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${Math.floor(diffMs / (1000 * 60 * 60 * 24))}d`;
  };

  return (
    <div className="glass-surface max-h-[360px] overflow-auto rounded-2xl border border-indigo-200/70 p-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-gradient-to-br from-indigo-100 to-violet-100 p-1.5">
            <Zap className="h-3.5 w-3.5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Recent Activity</h3>
            <p className="text-[11px] text-slate-500">Latest actions</p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleViewAll}
          className="flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-700"
        >
          View all
          <ArrowUpRight className="w-3 h-3" />
        </button>
      </div>

      <div className="space-y-1">
        {safeActivities.map((activity, index) => {
          const status = getStatusConfig(activity.status);
          const Icon = getActivityIcon(activity.type);

          return (
            <motion.button
              key={activity.id}
              type="button"
              onClick={() => handleActivityClick(activity)}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="group w-full cursor-pointer rounded-xl border border-indigo-100/80 bg-white/70 p-2.5 text-left transition-colors hover:border-indigo-200 focus:outline-none focus:ring-2 focus:ring-indigo-300"
            >
              <div className="flex items-start gap-3">
                <div className={`p-1.5 rounded-md ${status.bg}`}>
                  <Icon className={`w-3 h-3 ${status.color}`} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div>
                      <h4 className="line-clamp-1 text-xs font-medium text-slate-900">
                        {activity.title}
                      </h4>
                      <p className="mt-0.5 line-clamp-2 text-[11px] text-slate-500">
                        {activity.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-[11px] text-slate-500">
                        {formatTime(activity.time)}
                      </span>
                      <ChevronRight className="h-3 w-3 text-slate-400 transition-colors group-hover:text-indigo-600" />
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-1.5">
                    <span
                      className={`text-[11px] px-1.5 py-0.5 rounded ${status.bg} ${status.color}`}
                    >
                      {status.label}
                    </span>
                    {activity.metadata && (
                      <div className="flex items-center gap-2 text-[11px] text-slate-500">
                        {Object.entries(activity.metadata).map(
                          ([key, value], idx) => (
                            <React.Fragment key={key}>
                              {idx > 0 && (
                                <span className="text-slate-300">•</span>
                              )}
                              <span>{value}</span>
                            </React.Fragment>
                          ),
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      {safeActivities.length === 0 && (
        <div className="text-center py-8">
          <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-indigo-50 p-3">
            <Clock className="mx-auto h-6 w-6 text-indigo-400" />
          </div>
          <p className="text-sm text-slate-500">No recent activity</p>
        </div>
      )}
    </div>
  );
};

// Sample data if no props provided
RecentActivity.defaultProps = {
  activities: [
    {
      id: 1,
      type: "message",
      title: "Campaign Sent",
      description: "Promotional messages to 250 subscribers",
      time: new Date(Date.now() - 5 * 60000).toISOString(),
      status: "success",
      metadata: { Recipients: "250" },
    },
    {
      id: 2,
      type: "recurring",
      title: "Campaign Scheduled",
      description: "Holiday promotion for Dec 25th",
      time: new Date(Date.now() - 60 * 60000).toISOString(),
      status: "pending",
      metadata: { Date: "Dec 25" },
    },
    {
      id: 3,
      type: "template",
      title: "Template Created",
      description: "Birthday wishes template approved",
      time: new Date(Date.now() - 120 * 60000).toISOString(),
      status: "success",
      metadata: { Category: "Greeting" },
    },
    {
      id: 4,
      type: "group",
      title: "Group Integrated",
      description: "Joined Product Updates group",
      time: new Date(Date.now() - 300 * 60000).toISOString(),
      status: "success",
      metadata: { Members: "1.5K" },
    },
  ],
};

export default RecentActivity;
